'use client'

import type { FormFragment as OriginalFormFragment } from '@/lib/types/datocms/graphql/generated'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import { StructuredText } from 'react-datocms'
import Swoosh from '../svg/Swoosh'
import clsx from 'clsx'
import CtaTransparent from '../ui/cta/CtaTransparent'
import FormField from '../form/FormField'
import CtaDefault from '../ui/cta/CtaDefault'

type FormFragment = OriginalFormFragment & {
	swoosh?: boolean
	ctaTransparent?: boolean
	h2ClassName?: string
	descriptionClassName?: string
	style?: string | null | undefined
	footerForm?: boolean
	className?: string
}

export default function Form({
	columnsNumber,
	field,
	submitLink,
	submitLabel,
	swoosh,
	ctaTransparent,
	style: formStyle = 'box',
	h2ClassName,
	descriptionClassName,
	footerForm,
	className,
	file,
}: Readonly<FormFragment>) {
	const {
		handleSubmit,
		register,
		formState: { errors },
		reset,
		control,
	} = useForm()

	const [submitFeedback, setSubmitFeedback] = useState<string | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [key, setKey] = useState(0)

	const onSubmit = async (data: any) => {
		setLoading(true)
		if (!submitLink) return null

		try {
			const formData = new FormData()

			Object.keys(data).forEach((key) => {
				if (Array.isArray(data[key])) {
					if (data[key][0]?.value !== undefined) {
						// For multiple text inputs, join all values with commas
						const values = data[key]
							.map((item: { value: string }) => item.value)
							.filter((value: string) => value !== '')
							.join(', ')
						formData.append(key, values)
					} else {
						// For multiple choice checkboxes
						const selectedValues = data[key].join(', ')
						formData.append(key, selectedValues)
					}
				} else if (typeof data[key] === 'object' && data[key]?.value) {
					// For single object with value
					formData.append(key, data[key].value)
				} else {
					// For all other inputs including files
					const input = document.querySelector(`input[name="${key}"]`) as HTMLInputElement
					if (input?.type === 'file' && input.files?.[0]) {
						formData.append(key, input.files[0])
					} else {
						formData.append(key, data[key])
					}
				}
			})

			const response = await fetch(submitLink, {
				method: 'POST',
				body: formData,
			})

			if (response.ok) {
				// Reset react-hook-form state first
				reset()

				// Reset file inputs and their states
				const form = document.querySelector('form') as HTMLFormElement
				const fileInputs = form.querySelectorAll<HTMLInputElement>('input[type="file"]')
				fileInputs.forEach((input) => {
					input.value = ''
					const clearButton = input.closest('.form-component')?.querySelector('button')
					if (clearButton) {
						clearButton.click()
					}
				})

				// Reset select elements and their states
				const selects = form.querySelectorAll('select')
				selects.forEach((select) => {
					select.value = ''
					const event = new Event('change', { bubbles: true, cancelable: true })
					select.dispatchEvent(event)
				})

				// Force a re-render of the form to reset all component states
				setKey((prev) => prev + 1)

				// Reset controlled components
				control._reset()
				if (file) {
					const link = document.createElement('a')
					link.href = file.url
					link.target = '_blank'
					link.rel = 'noopener noreferrer'
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
				}

				setSubmitFeedback('Form submitted successfully')
			}
		} catch (error) {
			setSubmitFeedback('An error occurred while submitting the form')
		} finally {
			setLoading(false)
		}
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className={clsx(
				'flex flex-col gap-4 overflow-hidden relative [&>*]:z-10 @container',
				className
			)}
			encType="multipart/form-data" // Important for file uploads
		>
			{swoosh && <Swoosh className="absolute scale-[175%] translate-y-1/2 translate-x-20 z-0" />}

			<div
				className={clsx(
					'grid grid-cols-1',
					columnsNumber === '2' && '@xl:grid-cols-2',
					formStyle === 'line' ? 'gap-10' : 'gap-4'
				)}
			>
				{field.map((fieldItem, index) => {
					if (fieldItem.__typename === 'FormFieldRecord' && fieldItem?.fieldType) {
						return (
							<FormField
								key={`${fieldItem.id}-${index}`}
								keyField={`${fieldItem.id}-${index}`}
								field={fieldItem}
								register={register}
								errors={errors}
								formStyle={formStyle}
								footerForm={footerForm}
								loading={loading}
								control={control}
							/>
						)
					}
					if (fieldItem.__typename === 'FormInfoRecord') {
						return (
							<div className="@xl:col-span-2 first:mt-0 mt-10" key={fieldItem.id}>
								<h2 className={h2ClassName}>{fieldItem.title}</h2>
								{fieldItem.description && !isStructuredTextEmpty(fieldItem.description) && (
									<div className={clsx(descriptionClassName, 'mt-4')}>
										<StructuredText data={fieldItem.description.value} />
									</div>
								)}
							</div>
						)
					}
				})}
			</div>

			{!footerForm &&
				(ctaTransparent ? (
					<CtaTransparent
						className="ml-auto mt-8"
						disabled={loading}
						cta={{
							label: loading ? 'Submitting...' : submitLabel || 'Submit Form',
							__typename: 'NavigationItemRecord',
							id: 'submit-form',
							useExternalLink: false,
							openInNewWindow: false,
						}}
						type="submit"
					/>
				) : (
					<CtaDefault
						className="ml-auto mt-8"
						cta={{
							label: loading ? 'Submitting...' : submitLabel || 'Submit Form',
							__typename: 'NavigationItemRecord',
							id: 'submit-form',
							useExternalLink: false,
							openInNewWindow: false,
						}}
						disabled={loading}
						type="submit"
					/>
				))}

			{submitFeedback && (
				<p className="bg-white-frosted p-5 rounded-lg mt-8 text-center text-blue">
					{submitFeedback}
				</p>
			)}
		</form>
	)
}
